import type { PostHog, Survey } from "posthog-js";
import { useEffect, useMemo, useState } from "react";

import {
  REASONS,
  REPORT_TEXT_LIMIT,
  ReportInput,
  SubmitReport,
} from "./reports";

// Key of the rollout flag. The report survey is linked to it, and each response
// carries it as `reportSource` for dashboards.
export const USER_REPORT_KEY = "ignition-user-reported-errors";

// Ends a hung request, so the user sees the failed state and can try again.
const SUBMIT_TIMEOUT_MS = 10000;

type ReportSurvey = {
  id: string;
  questions: { id: string; question: string }[];
  reasonId: string;
  detailsId: string;
  // One choice per reason, in REASONS order.
  choices: string[];
};

/**
 * Finds the report survey among the surveys PostHog says are active: an API survey
 * linked to the rollout flag, with the reason as a single choice first and optional
 * details as open text second.
 */
export const findReportSurvey = (
  surveys: Survey[]
): ReportSurvey | undefined => {
  const survey = surveys.find(
    (candidate) =>
      candidate.type === "api" && candidate.linked_flag_key === USER_REPORT_KEY
  );
  const [reason, details] = survey?.questions ?? [];
  if (
    !survey ||
    !reason?.id ||
    !details?.id ||
    reason.type !== "single_choice" ||
    details.type !== "open" ||
    reason.choices.length < REASONS.length
  ) {
    return;
  }

  return {
    id: survey.id,
    questions: survey.questions.map(({ id, question }) => ({
      id: id as string,
      question,
    })),
    reasonId: reason.id,
    detailsId: details.id,
    choices: reason.choices,
  };
};

/** Builds the properties PostHog reads as a response to the survey. */
export const getSurveyResponse = (
  survey: ReportSurvey,
  input: ReportInput
) => ({
  $survey_id: survey.id,
  $survey_submission_id: input.submissionId,
  $survey_completed: true,
  $survey_questions: survey.questions,
  // Matched by position, so reordering or removing survey choices mislabels reports.
  [`$survey_response_${survey.reasonId}`]:
    survey.choices[REASONS.indexOf(input.reason)],
  // Details are only asked for after "Something else".
  ...(input.reason === "other" && input.details.trim()
    ? {
        [`$survey_response_${survey.detailsId}`]: input.details.slice(
          0,
          REPORT_TEXT_LIMIT
        ),
      }
    : {}),
});

const isCollecting = (posthog: PostHog) =>
  posthog.isFeatureEnabled(USER_REPORT_KEY, { send_event: false }) === true &&
  !posthog.has_opted_out_capturing();

/**
 * Returns a submitter once the user has the rollout flag and the report survey is
 * active, and undefined before that. Responses go straight to PostHog ingestion so
 * the user sees a failure; the SDK's capture() reports no result.
 */
export const useReportSurvey = (
  posthog: PostHog | undefined,
  {
    integration,
    carrierId,
    clientId,
  }: { integration?: string; carrierId?: string; clientId?: string }
): SubmitReport | undefined => {
  const [survey, setSurvey] = useState<ReportSurvey>();

  useEffect(() => {
    if (!posthog) return;
    let stale = false;
    const refresh = () => {
      if (!isCollecting(posthog)) {
        setSurvey(undefined);
        return;
      }
      posthog.getActiveMatchingSurveys((surveys) => {
        if (!stale) setSurvey(findReportSurvey(surveys));
      });
    };
    // Either the flags or the surveys can load first.
    const stopFlags = posthog.onFeatureFlags(refresh);
    const stopSurveys = posthog.onSurveysLoaded(refresh);
    refresh();
    return () => {
      stale = true;
      stopFlags();
      stopSurveys();
    };
  }, [posthog]);

  return useMemo<SubmitReport | undefined>(() => {
    if (!posthog || !survey) return undefined;

    return async (ignitionToken, input) => {
      if (!isCollecting(posthog)) throw new Error("Feedback is unavailable");
      const { token, api_host: apiHost } = posthog.config;
      // Events go to the ingestion host, not the app host the SDK may be set to.
      const host = apiHost
        .replace(/\/$/, "")
        .replace(
          /^https:\/\/(app|us)\.posthog\.com$/,
          "https://us.i.posthog.com"
        );
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), SUBMIT_TIMEOUT_MS);
      try {
        const response = await fetch(`${host}/i/v0/e/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "omit",
          signal: controller.signal,
          body: JSON.stringify({
            api_key: token,
            distinct_id: posthog.get_distinct_id(),
            // Reuse the event ID so PostHog can deduplicate unchanged retries.
            uuid: input.submissionId,
            event: "survey sent",
            timestamp: input.createdAt,
            properties: {
              ...getSurveyResponse(survey, input),
              schemaVersion: 1,
              createdAt: input.createdAt,
              reportSource: USER_REPORT_KEY,
              reportId: input.submissionId,
              ignitionToken,
              // Lets the report link to the session replay, as the SDK's own events do.
              $session_id: posthog.get_session_id(),
              // posthog-js has no documented getter for the window id; this reads the SDK's
              // session manager, so check it when upgrading posthog-js.
              $window_id:
                posthog.sessionManager?.checkAndGetSessionAndWindowId(true)
                  .windowId,
              reportingArea: input.area,
              reportProblem: input.reason,
              step: input.step,
              reportDetails:
                input.reason === "other"
                  ? input.details.slice(0, REPORT_TEXT_LIMIT)
                  : undefined,
              integration: input.attempt?.integration ?? integration ?? null,
              carrierId: input.attempt?.carrierId ?? carrierId ?? null,
              ...(input.area === "login"
                ? {
                    loginPath: input.attempt?.loginPath ?? null,
                    loginErrorCode: input.attempt?.loginErrorCode ?? null,
                  }
                : {}),
              ...(input.area === "document-upload"
                ? {
                    documentRef: input.attempt?.documentRef ?? null,
                    documentClassification:
                      input.attempt?.documentClassification ?? null,
                  }
                : {}),
              // Plain property so the dashboard filter can pick a client.
              clientId: clientId ?? null,
              $groups: clientId ? { client: clientId } : undefined,
            },
          }),
        });
        if (!response.ok) throw new Error("Feedback submission failed");
      } finally {
        clearTimeout(timeout);
      }
    };
  }, [posthog, survey, integration, carrierId, clientId]);
};
